import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSiteContent, useStaticTranslation } from '../../context/SiteContentContext';

const LOGO_URL = '/images/logo.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, switchLanguage, localizedPath } = useSiteContent();
  const { st } = useStaticTranslation();

  const navigation = [
    { name: st('nav.home'), href: localizedPath('/') },
    { name: st('nav.about'), href: localizedPath('/about') },
    { name: st('nav.services'), href: localizedPath('/services') },
    { name: st('nav.fleet'), href: localizedPath('/fleet') },
    { name: st('nav.destinations'), href: localizedPath('/destinations') },
    { name: st('nav.whyChooseUs'), href: localizedPath('/why-choose-us') },
    { name: st('nav.ldmCalculator'), href: localizedPath('/ldm-calculator') },
    { name: st('nav.careers'), href: localizedPath('/careers') },
    { name: st('nav.news'), href: localizedPath('/news') },
    { name: st('nav.faq'), href: localizedPath('/faq') },
    { name: st('nav.contact'), href: localizedPath('/contact') },
  ];

  // Check if current path matches nav item (accounting for language prefix)
  const isActive = (href) => {
    const currentPath = location.pathname;
    if (language === 'en') {
      return currentPath === href || (href === '/en' && currentPath === '/en');
    }
    return currentPath === href;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-slate-200/50'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo - Left aligned */}
            <Link 
              to={localizedPath('/')} 
              data-testid="logo-link"
              className="flex items-center group flex-shrink-0"
            >
              <img 
                src={LOGO_URL} 
                alt="TetraLog International Transport Solutions" 
                className="h-12 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation - Centered */}
            <nav className="hidden xl:flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2" data-testid="desktop-nav">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`nav-link px-3 py-2 text-sm font-medium transition-all duration-200 rounded-full ${
                    isActive(item.href)
                      ? 'text-[#C41E3A] bg-red-50'
                      : 'text-slate-600 hover:text-[#C41E3A] hover:bg-slate-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {/* Language Switcher - Desktop */}
              <div className="hidden sm:flex items-center gap-1 bg-slate-100/80 rounded-full p-1">
                <button
                  onClick={() => switchLanguage('en')}
                  data-testid="lang-en-btn"
                  className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all duration-200 ${
                    language === 'en' 
                      ? 'bg-[#C41E3A] text-white shadow-md' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => switchLanguage('bg')}
                  data-testid="lang-bg-btn"
                  className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all duration-200 ${
                    language === 'bg' 
                      ? 'bg-[#C41E3A] text-white shadow-md' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  BG
                </button>
              </div>

              {/* Language Switcher - Mobile (visible in header) */}
              <div className="flex sm:hidden items-center gap-1 bg-slate-100/80 rounded-full p-0.5">
                <button
                  onClick={() => switchLanguage('en')}
                  data-testid="lang-en-btn-mobile"
                  className={`px-2 py-1 text-xs font-bold rounded-full transition-all duration-200 ${
                    language === 'en' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'text-slate-600'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => switchLanguage('bg')}
                  data-testid="lang-bg-btn-mobile"
                  className={`px-2 py-1 text-xs font-bold rounded-full transition-all duration-200 ${
                    language === 'bg' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'text-slate-600'
                  }`}
                >
                  BG
                </button>
              </div>

              {/* CTA Button - Styled */}
              <Link to={localizedPath('/contact') + '?quote=true#quote-form'}>
                <button 
                  data-testid="header-quote-btn"
                  className="btn-modern btn-primary btn-sm btn-shimmer hidden sm:flex"
                >
                  <span>{st('nav.getQuote')}</span>
                </button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                data-testid="mobile-menu-toggle"
                className="xl:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-50 bg-white xl:hidden menu-enter"
          data-testid="mobile-menu"
        >
          {/* Header - same as closed menu */}
          <div className="flex items-center justify-between px-4 h-16 border-b border-slate-100 bg-white">
            <img src={LOGO_URL} alt="TetraLog" className="h-12 w-auto" />
            
            <div className="flex items-center gap-2">
              {/* Language Switcher */}
              <div className="flex items-center gap-1 bg-slate-100/80 rounded-full p-0.5">
                <button
                  onClick={() => switchLanguage('en')}
                  className={`px-2 py-1 text-xs font-bold rounded-full transition-all duration-200 ${
                    language === 'en' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'text-slate-600'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => switchLanguage('bg')}
                  className={`px-2 py-1 text-xs font-bold rounded-full transition-all duration-200 ${
                    language === 'bg' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'text-slate-600'
                  }`}
                >
                  BG
                </button>
              </div>

              {/* Get Quote Button */}
              <Link to={localizedPath('/contact') + '?quote=true#quote-form'} onClick={() => setIsMobileMenuOpen(false)}>
                <button className="btn-modern btn-primary btn-sm btn-shimmer">
                  <span>{st('nav.getQuote')}</span>
                </button>
              </Link>

              {/* Close Button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-all duration-200"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          {/* Navigation Links */}
          <div className="flex flex-col items-center justify-start pt-2 pb-4 px-4 overflow-y-auto h-[calc(100vh-64px)]">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full text-center text-base font-bold tracking-tight py-1.5 rounded-xl transition-all duration-200 menu-item-animate ${
                  isActive(item.href)
                    ? 'text-[#C41E3A] bg-red-50'
                    : 'text-slate-800 hover:text-[#C41E3A] hover:bg-slate-50'
                }`}
                style={{ animationDelay: `${index * 0.03}s` }}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Language & Get Quote - Bottom */}
            <div className="mt-4 flex flex-col items-center gap-3 w-full menu-item-animate pb-4" style={{ animationDelay: '0.4s' }}>
              <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
                <button
                  onClick={() => switchLanguage('en')}
                  className={`px-4 py-2 text-sm font-bold rounded-full transition-all duration-200 ${
                    language === 'en' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => switchLanguage('bg')}
                  className={`px-4 py-2 text-sm font-bold rounded-full transition-all duration-200 ${
                    language === 'bg' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Български
                </button>
              </div>
              <Link to={localizedPath('/contact') + '?quote=true#quote-form'} className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                <button className="w-full btn-modern btn-primary py-3 text-base">
                  {st('nav.getQuote')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
